/**
 * Generated by orval v8.4.2 🍺
 * Do not edit manually.
 * The SKY API
 * OpenAPI spec version: 1.0.0
 */
import type {
  MutationFunction,
  QueryClient,
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";

import type {
  AuthenticateUser200,
  AuthenticateUserBody,
  BadRequestResponse,
  CompleteNewPassword200,
  CompleteNewPasswordBody,
  ForbiddenResponse,
  ForgotPassword200,
  ForgotPasswordBody,
  InternalServerErrorResponse,
  LogoutUser200,
  NotFoundResponse,
  PasswordlessInitiate200,
  PasswordlessInitiateBody,
  PasswordlessVerify200,
  PasswordlessVerifyBody,
  RefreshAccessToken200,
  RefreshAccessTokenBody,
  ResetPassword200,
  ResetPasswordBody,
  SocialRegister200,
  SocialRegister201,
  SocialRegister403,
  SocialRegisterBody,
  UnauthorizedResponse,
} from ".././model";

import { customInstance } from "../../custom-instance";

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

/**
 * This endpoint allows a user to log in by providing their email and password. Upon successful authentication, it returns a JWT token, which can be used for authorization in subsequent requests.
 * @summary Authenticate a user and return a JWT token
 */
export const authenticateUser = (
  authenticateUserBody: AuthenticateUserBody,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<AuthenticateUser200>(
    {
      url: `https://api.app.usesky.ai/login`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: authenticateUserBody,
      signal,
    },
    options
  );
};

export const getAuthenticateUserMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | InternalServerErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof authenticateUser>>,
    TError,
    { data: AuthenticateUserBody },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof authenticateUser>>,
  TError,
  { data: AuthenticateUserBody },
  TContext
> => {
  const mutationKey = ["authenticateUser"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof authenticateUser>>,
    { data: AuthenticateUserBody }
  > = (props) => {
    const { data } = props ?? {};

    return authenticateUser(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AuthenticateUserMutationResult = NonNullable<
  Awaited<ReturnType<typeof authenticateUser>>
>;
export type AuthenticateUserMutationBody = AuthenticateUserBody;
export type AuthenticateUserMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | InternalServerErrorResponse;

/**
 * @summary Authenticate a user and return a JWT token
 */
export const useAuthenticateUser = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | InternalServerErrorResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof authenticateUser>>,
      TError,
      { data: AuthenticateUserBody },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof authenticateUser>>,
  TError,
  { data: AuthenticateUserBody },
  TContext
> => {
  return useMutation(getAuthenticateUserMutationOptions(options), queryClient);
};
/**
 * Starts passwordless authentication flow by sending an OTP code to the user's email.
Uses Cognito Custom Auth flow with Lambda triggers.

 * @summary Initiate passwordless authentication
 */
export const passwordlessInitiate = (
  passwordlessInitiateBody: PasswordlessInitiateBody,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<PasswordlessInitiate200>(
    {
      url: `https://api.app.usesky.ai/auth/passwordless/initiate`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: passwordlessInitiateBody,
      signal,
    },
    options
  );
};

export const getPasswordlessInitiateMutationOptions = <
  TError = BadRequestResponse | InternalServerErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof passwordlessInitiate>>,
    TError,
    { data: PasswordlessInitiateBody },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof passwordlessInitiate>>,
  TError,
  { data: PasswordlessInitiateBody },
  TContext
> => {
  const mutationKey = ["passwordlessInitiate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof passwordlessInitiate>>,
    { data: PasswordlessInitiateBody }
  > = (props) => {
    const { data } = props ?? {};

    return passwordlessInitiate(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PasswordlessInitiateMutationResult = NonNullable<
  Awaited<ReturnType<typeof passwordlessInitiate>>
>;
export type PasswordlessInitiateMutationBody = PasswordlessInitiateBody;
export type PasswordlessInitiateMutationError =
  | BadRequestResponse
  | InternalServerErrorResponse;

/**
 * @summary Initiate passwordless authentication
 */
export const usePasswordlessInitiate = <
  TError = BadRequestResponse | InternalServerErrorResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof passwordlessInitiate>>,
      TError,
      { data: PasswordlessInitiateBody },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof passwordlessInitiate>>,
  TError,
  { data: PasswordlessInitiateBody },
  TContext
> => {
  return useMutation(
    getPasswordlessInitiateMutationOptions(options),
    queryClient
  );
};
/**
 * Verifies the OTP code sent to user's email and returns authentication tokens.

 * @summary Verify OTP code for passwordless authentication
 */
export const passwordlessVerify = (
  passwordlessVerifyBody: PasswordlessVerifyBody,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<PasswordlessVerify200>(
    {
      url: `https://api.app.usesky.ai/auth/passwordless/verify`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: passwordlessVerifyBody,
      signal,
    },
    options
  );
};

export const getPasswordlessVerifyMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | InternalServerErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof passwordlessVerify>>,
    TError,
    { data: PasswordlessVerifyBody },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof passwordlessVerify>>,
  TError,
  { data: PasswordlessVerifyBody },
  TContext
> => {
  const mutationKey = ["passwordlessVerify"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof passwordlessVerify>>,
    { data: PasswordlessVerifyBody }
  > = (props) => {
    const { data } = props ?? {};

    return passwordlessVerify(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PasswordlessVerifyMutationResult = NonNullable<
  Awaited<ReturnType<typeof passwordlessVerify>>
>;
export type PasswordlessVerifyMutationBody = PasswordlessVerifyBody;
export type PasswordlessVerifyMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | InternalServerErrorResponse;

/**
 * @summary Verify OTP code for passwordless authentication
 */
export const usePasswordlessVerify = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | InternalServerErrorResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof passwordlessVerify>>,
      TError,
      { data: PasswordlessVerifyBody },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof passwordlessVerify>>,
  TError,
  { data: PasswordlessVerifyBody },
  TContext
> => {
  return useMutation(
    getPasswordlessVerifyMutationOptions(options),
    queryClient
  );
};
/**
 * Completes the registration process for users who signed in via Google Workspace or Microsoft 365.
Creates an organization and associates the user with it.
Blocks consumer email domains (gmail.com, outlook.com, etc.) - only business accounts allowed.

 * @summary Complete registration for social login users
 */
export const socialRegister = (
  socialRegisterBody: SocialRegisterBody,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<SocialRegister200 | SocialRegister201>(
    {
      url: `https://api.app.usesky.ai/auth/social/register`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: socialRegisterBody,
      signal,
    },
    options
  );
};

export const getSocialRegisterMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | SocialRegister403
    | NotFoundResponse
    | InternalServerErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof socialRegister>>,
    TError,
    { data: SocialRegisterBody },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof socialRegister>>,
  TError,
  { data: SocialRegisterBody },
  TContext
> => {
  const mutationKey = ["socialRegister"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof socialRegister>>,
    { data: SocialRegisterBody }
  > = (props) => {
    const { data } = props ?? {};

    return socialRegister(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SocialRegisterMutationResult = NonNullable<
  Awaited<ReturnType<typeof socialRegister>>
>;
export type SocialRegisterMutationBody = SocialRegisterBody;
export type SocialRegisterMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | SocialRegister403
  | NotFoundResponse
  | InternalServerErrorResponse;

/**
 * @summary Complete registration for social login users
 */
export const useSocialRegister = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | SocialRegister403
    | NotFoundResponse
    | InternalServerErrorResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof socialRegister>>,
      TError,
      { data: SocialRegisterBody },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof socialRegister>>,
  TError,
  { data: SocialRegisterBody },
  TContext
> => {
  return useMutation(getSocialRegisterMutationOptions(options), queryClient);
};
/**
 * @summary Removes the current session
 */
export const logoutUser = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<LogoutUser200>(
    { url: `https://api.app.usesky.ai/logout`, method: "POST", signal },
    options
  );
};

export const getLogoutUserMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof logoutUser>>,
    TError,
    void,
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof logoutUser>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ["logoutUser"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof logoutUser>>,
    void
  > = () => {
    return logoutUser(requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type LogoutUserMutationResult = NonNullable<
  Awaited<ReturnType<typeof logoutUser>>
>;

export type LogoutUserMutationError = unknown;

/**
 * @summary Removes the current session
 */
export const useLogoutUser = <TError = unknown, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof logoutUser>>,
      TError,
      void,
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof logoutUser>>,
  TError,
  void,
  TContext
> => {
  return useMutation(getLogoutUserMutationOptions(options), queryClient);
};
/**
 * @summary Refresh the access token using a refresh token
 */
export const refreshAccessToken = (
  refreshAccessTokenBody: RefreshAccessTokenBody,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<RefreshAccessToken200>(
    {
      url: `https://api.app.usesky.ai/refresh-token`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: refreshAccessTokenBody,
      signal,
    },
    options
  );
};

export const getRefreshAccessTokenMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | InternalServerErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof refreshAccessToken>>,
    TError,
    { data: RefreshAccessTokenBody },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof refreshAccessToken>>,
  TError,
  { data: RefreshAccessTokenBody },
  TContext
> => {
  const mutationKey = ["refreshAccessToken"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof refreshAccessToken>>,
    { data: RefreshAccessTokenBody }
  > = (props) => {
    const { data } = props ?? {};

    return refreshAccessToken(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RefreshAccessTokenMutationResult = NonNullable<
  Awaited<ReturnType<typeof refreshAccessToken>>
>;
export type RefreshAccessTokenMutationBody = RefreshAccessTokenBody;
export type RefreshAccessTokenMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | InternalServerErrorResponse;

/**
 * @summary Refresh the access token using a refresh token
 */
export const useRefreshAccessToken = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | InternalServerErrorResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof refreshAccessToken>>,
      TError,
      { data: RefreshAccessTokenBody },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof refreshAccessToken>>,
  TError,
  { data: RefreshAccessTokenBody },
  TContext
> => {
  return useMutation(
    getRefreshAccessTokenMutationOptions(options),
    queryClient
  );
};
/**
 * @summary Complete the new password challenge for first-time login
 */
export const completeNewPassword = (
  completeNewPasswordBody: CompleteNewPasswordBody,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<CompleteNewPassword200>(
    {
      url: `https://api.app.usesky.ai/complete-new-password`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: completeNewPasswordBody,
      signal,
    },
    options
  );
};

export const getCompleteNewPasswordMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | InternalServerErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof completeNewPassword>>,
    TError,
    { data: CompleteNewPasswordBody },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof completeNewPassword>>,
  TError,
  { data: CompleteNewPasswordBody },
  TContext
> => {
  const mutationKey = ["completeNewPassword"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof completeNewPassword>>,
    { data: CompleteNewPasswordBody }
  > = (props) => {
    const { data } = props ?? {};

    return completeNewPassword(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CompleteNewPasswordMutationResult = NonNullable<
  Awaited<ReturnType<typeof completeNewPassword>>
>;
export type CompleteNewPasswordMutationBody = CompleteNewPasswordBody;
export type CompleteNewPasswordMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | InternalServerErrorResponse;

/**
 * @summary Complete the new password challenge for first-time login
 */
export const useCompleteNewPassword = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | InternalServerErrorResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof completeNewPassword>>,
      TError,
      { data: CompleteNewPasswordBody },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof completeNewPassword>>,
  TError,
  { data: CompleteNewPasswordBody },
  TContext
> => {
  return useMutation(
    getCompleteNewPasswordMutationOptions(options),
    queryClient
  );
};
/**
 * @summary Initiate password reset process
 */
export const forgotPassword = (
  forgotPasswordBody: ForgotPasswordBody,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<ForgotPassword200>(
    {
      url: `https://api.app.usesky.ai/forgot-password`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: forgotPasswordBody,
      signal,
    },
    options
  );
};

export const getForgotPasswordMutationOptions = <
  TError = BadRequestResponse | NotFoundResponse | InternalServerErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof forgotPassword>>,
    TError,
    { data: ForgotPasswordBody },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof forgotPassword>>,
  TError,
  { data: ForgotPasswordBody },
  TContext
> => {
  const mutationKey = ["forgotPassword"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof forgotPassword>>,
    { data: ForgotPasswordBody }
  > = (props) => {
    const { data } = props ?? {};

    return forgotPassword(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ForgotPasswordMutationResult = NonNullable<
  Awaited<ReturnType<typeof forgotPassword>>
>;
export type ForgotPasswordMutationBody = ForgotPasswordBody;
export type ForgotPasswordMutationError =
  | BadRequestResponse
  | NotFoundResponse
  | InternalServerErrorResponse;

/**
 * @summary Initiate password reset process
 */
export const useForgotPassword = <
  TError = BadRequestResponse | NotFoundResponse | InternalServerErrorResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof forgotPassword>>,
      TError,
      { data: ForgotPasswordBody },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof forgotPassword>>,
  TError,
  { data: ForgotPasswordBody },
  TContext
> => {
  return useMutation(getForgotPasswordMutationOptions(options), queryClient);
};
/**
 * @summary Complete password reset with verification code
 */
export const resetPassword = (
  resetPasswordBody: ResetPasswordBody,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<ResetPassword200>(
    {
      url: `https://api.app.usesky.ai/reset-password`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: resetPasswordBody,
      signal,
    },
    options
  );
};

export const getResetPasswordMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | NotFoundResponse
    | InternalServerErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof resetPassword>>,
    TError,
    { data: ResetPasswordBody },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof resetPassword>>,
  TError,
  { data: ResetPasswordBody },
  TContext
> => {
  const mutationKey = ["resetPassword"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof resetPassword>>,
    { data: ResetPasswordBody }
  > = (props) => {
    const { data } = props ?? {};

    return resetPassword(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ResetPasswordMutationResult = NonNullable<
  Awaited<ReturnType<typeof resetPassword>>
>;
export type ResetPasswordMutationBody = ResetPasswordBody;
export type ResetPasswordMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | NotFoundResponse
  | InternalServerErrorResponse;

/**
 * @summary Complete password reset with verification code
 */
export const useResetPassword = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | NotFoundResponse
    | InternalServerErrorResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof resetPassword>>,
      TError,
      { data: ResetPasswordBody },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof resetPassword>>,
  TError,
  { data: ResetPasswordBody },
  TContext
> => {
  return useMutation(getResetPasswordMutationOptions(options), queryClient);
};
